import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { UnifiedMarkdownRenderer } from "@/components/ui/unified-markdown-renderer";
import { Template } from "@/types";
import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";

interface TemplatePreviewDialogProps {
  template: Template;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const TemplatePreviewDialog = ({ template, open, onOpenChange }: TemplatePreviewDialogProps) => {
  const { t } = useTranslation();
  const hasPreviewContent = template.preview_template_result && template.preview_template_result.trim() !== '';
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkIsMobile = () => {
      setIsMobile(window.innerWidth <= 768 || 'ontouchstart' in window);
    };

    checkIsMobile();
    window.addEventListener('resize', checkIsMobile);

    return () => window.removeEventListener('resize', checkIsMobile);
  }, []);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className={`overflow-hidden flex flex-col ${isMobile
          ? 'w-screen h-screen max-w-none max-h-none m-0 rounded-none border-0'
          : 'max-w-4xl max-h-[90vh]'
        }`}>
        <DialogHeader className={isMobile ? 'pb-2' : ''}>
          <DialogTitle className={`font-semibold ${isMobile ? 'text-lg' : 'text-xl'
            }`}>
            {t('templates.preview.title', { name: template.name })}
          </DialogTitle>
          {template.description && (
            <p className={`text-muted-foreground ${isMobile ? 'text-xs mt-1' : 'text-sm mt-2'
              }`}>
              {template.description}
            </p>
          )}
        </DialogHeader>

        <div className="flex-1 overflow-hidden">
          {hasPreviewContent ? (
            <div className={isMobile ? 'mt-2' : 'mt-4'}>
              <UnifiedMarkdownRenderer
                value={template.preview_template_result!}
                mode="view"
                className={`${isMobile ? 'min-h-[70vh] max-h-[70vh] overflow-y-auto' : 'min-h-[300px] max-h-[60vh] overflow-y-auto'} ${isMobile ? 'text-sm' : ''
                  }`}
              />
            </div>
          ) : (
            <div className={`flex items-center justify-center text-center ${isMobile ? 'h-[70vh]' : 'h-64'
              }`}>
              <div className="text-muted-foreground">
                <div className={`font-medium mb-2 ${isMobile ? 'text-base' : 'text-lg'
                  }`}>
                  {t('templates.preview.no_data_title')}
                </div>
                <p className={isMobile ? 'text-xs' : 'text-sm'}>
                  {t('templates.preview.no_data_desc')}
                </p>
              </div>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default TemplatePreviewDialog;